import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { handleLinkClick } from "@/Config/helper";
import styles from "./NavBar.module.css";

interface FlexibleMegamenuProps {
  onClose: () => void;
  megamenuData?: any; // Accept data as prop instead of fetching
}

const FlexibleMegamenu: React.FC<FlexibleMegamenuProps> = ({
  onClose,
  megamenuData,
}) => {
  const router = useRouter();

  const handleItemClick = (url: string) => {
    onClose();
    router.push(url);
  };

  // Helper function to check if a section has content
  const hasSectionContent = (section: any) => {
    // Show section if it has a title OR if it has items
    return (
      (section?.title && section.title.trim() !== "") ||
      (section?.items && section.items.length > 0)
    );
  };

  // Helper function to check if a category has any content
  const hasCategoryContent = (category: any) => {
    // Show category if it has a title, regardless of section content
    return category?.title && category.title.trim() !== "";
  };

  // Show empty state if no data
  if (!megamenuData) {
    return (
      <div className={styles.flexibleMegamenu}>
        <div className={styles.megamenuGrid}>
          <div className={styles.megamenuColumn}>
            <div>No menu data available</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.flexibleMegamenu}>
      <div className={styles.megamenuGrid}>
        {/* First Column - Law School, Economics, Technology */}
        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(0, 3)
            .map((category: any, index: number) => {
              // Only render category if it has content
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    {category?.title && <h3>{category.title}</h3>}
                  </Link>
                  {category?.sections?.map((section: any) => {
                    // Only render section if it has content
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any) => (
                          <Link
                            key={item?.label}
                            href={item?.url || "#"}
                            className={styles.submenuItem}
                            onClick={() => handleItemClick(item?.url || "#")}
                          >
                            {item?.label}
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                  {index < 2 &&
                    hasCategoryContent(
                      megamenuData?.categories?.[index + 1]
                    ) && <div className={styles.categoryDivider} />}
                </div>
              );
            })}
        </div>

        {/* Second Column - Business School, Entrepreneurship */}
        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(3, 5)
            .map((category: any, index: number) => {
              // Only render category if it has content
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    <h3>{category?.title}</h3>
                  </Link>
                  {category?.sections?.map((section: any) => {
                    // Only render section if it has content
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any) => (
                          <Link
                            key={item?.label}
                            href={item?.url || "#"}
                            className={styles.submenuItem}
                            onClick={() => handleItemClick(item?.url || "#")}
                          >
                            {item?.label}
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                  {index < 1 &&
                    hasCategoryContent(megamenuData?.categories?.[4]) && (
                      <div className={styles.categoryDivider} />
                    )}
                </div>
              );
            })}
        </div>

        {/* Third Column - Public Management, Education */}
        <div className={styles.megamenuColumn}>
          {megamenuData?.categories
            ?.slice(5, 10)
            .map((category: any, index: number) => {
              // Only render category if it has content
              if (!hasCategoryContent(category)) return null;

              return (
                <div key={category?.title} className={styles.megamenuCategory}>
                  <Link
                    href={category?.url || "#"}
                    className={styles.categoryTitle}
                    onClick={(e) =>
                      handleLinkClick(e, category?.url || "#", router)
                    }
                  >
                    <h3>{category?.title}</h3>
                  </Link>
                  {category?.sections?.map((section: any) => {
                    // Only render section if it has content
                    if (!hasSectionContent(section)) return null;

                    return (
                      <div
                        key={section?.title}
                        className={styles.megamenuSection}
                      >
                        {section?.title && section.title.trim() !== "" && (
                          <Link
                            href={section?.url || "#"}
                            className={styles.sectionTitle}
                            onClick={(e) =>
                              handleLinkClick(e, section?.url || "#", router)
                            }
                          >
                            {section?.title && <h4>{section.title}</h4>}
                          </Link>
                        )}
                        {section?.items?.map((item: any) => (
                          <Link
                            key={item?.label}
                            href={item?.url || "#"}
                            className={styles.submenuItem}
                            onClick={() => handleItemClick(item?.url || "#")}
                          >
                            {item?.label}
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                  {index < 1 &&
                    hasCategoryContent(megamenuData?.categories?.[6]) && (
                      <div className={styles.categoryDivider} />
                    )}
                </div>
              );
            })}
        </div>

        {/* Fourth Column - Certification Courses */}
        <div className={styles.megamenuColumn}>
          {megamenuData?.certificationCourses?.items?.length > 0 && (
            <div className={styles.megamenuCategory}>
              <Link
                href={megamenuData?.certificationCourses?.url || "#"}
                className={styles.categoryTitle}
                onClick={(e) =>
                  handleLinkClick(
                    e,
                    megamenuData?.certificationCourses?.url || "#",
                    router
                  )
                }
              >
                <h3>{megamenuData?.certificationCourses?.title}</h3>
              </Link>
              <div className={styles.certificationGrid}>
                {megamenuData?.certificationCourses?.items?.map(
                  (course: any) => (
                    <Link
                      key={course?.label}
                      href={course?.url || "#"}
                      className={styles.submenuItem}
                      onClick={() => handleItemClick(course?.url || "#")}
                    >
                      {course?.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlexibleMegamenu;
